import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";

import { useBreadcrumb } from "hooks/useBreadcrumb";
import { stringFormattingToCurrency } from "js/formatting";
import getPaymentUID from "service/paymentServices/getPaymentUID";

import PaymentPolicy from "components/UnauthorizedUser/MakeAPayment/PaymentPolicy";
import PaymentAmount from "components/UnauthorizedUser/MakeAPayment/PaymentAmount";
import PaymentMethod from "components/UnauthorizedUser/MakeAPayment/PaymentMethod";
import PaymentPayor from "components/UnauthorizedUser/MakeAPayment/PaymentPayor";
import UnauthCustomerAuthorizePaymentModal from "components/UnauthorizedUser/MakeAPayment/UnauthCustomerAuthorizePaymentModal";
import ConfirmModal from "components/ModalDialogs/ConfirmModal";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";

import Toast from "components/General/Toast";
import AuthorizePaymentNameValidationModal from "components/ModalDialogs/AuthorizePaymentNameValidationModal";
import { isFlagEnabled } from "js/utilities";
import config from "config/config";

export default function UnauthPayment() {
  let navigate = useNavigate();
  const { t } = useTranslation();
  const {
    register,
    setValue,
    trigger,
    getFieldState,
    getValues,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();
  const { user } = useSelector((s) => s.user);
  const [userValErrorModalState, setUserValErrorModalState] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [authorizeModal, setAuthorizeModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [uid, setUid] = useState();
  const [title, setTitle] = useState();
  const [disc, setDisc] = useState();
  const [policy, setPolicy] = useState();
  const [isNonNaturalEntity, setIsNonNaturalEntity] = useState(false);

  const makeAPaymentUnauthForm = useRef();

  const [
    creditCardValidationCheckModalState,
    setCreditCardValidationCheckModalState,
  ] = useState(false);
  const [toastDetail, setToastDetail] = useState({
    title: "title",
    message: "message",
    type: undefined,
  });

  const [premiumAmount, setPremiumAmount] = useState();

  const cancel = () => {
    navigate("/policies", { replace: true });
  };
  async function advanceModal() {
    setPremiumAmount(getValues("amount")?.replace("$", "")?.replace(",", ""));
    if (isFlagEnabled("ENABLE_NAME_VALIDATION_ON_AUTHORIZE_PAYMENT_MODAL"))
      return setCreditCardValidationCheckModalState(true);

    const policyNumber = getValues("policyNumber");
    let fullName;
    if (getValues("mInitial").trim()) {
      fullName = `${getValues("firstName")} ${getValues(
        "mInitial"
      )} ${getValues("lastName")}`;
    } else {
      fullName = `${getValues("firstName")} ${getValues("lastName")}`;
    }

    if (parseFloat(getValues("amount").replace("$", "").replace(",", "")) > 0) {
      if (policyNumber && fullName) {
        let response = await getPaymentUID(
          premiumAmount,
          `${policy?.contractId}`,
          `${fullName}`,
          "customer",
          `${user?.userId}`,
          `${policy?.blockCode}`
        ).catch((error) => {
          console.log(error);
          setTitle(t("MakePayment-errorTitle1"));
          setDisc(t("MakePayment-errorSubTitle1"));
          setUserValErrorModalState(true);
        });
        if (response?.uid) {
          setUid(response?.uid);
          setAuthorizeModal(true);
          setConfirmationModal(false);
        } else {
          //display user friendly error message to user
          setTitle(t("MakePayment-errorTitle1"));
          setDisc(t("MakePayment-errorSubTitle1"));
          setUserValErrorModalState(true);
        }
      } else {
        setTitle(t("MakePayment-errorTitle1"));
        setDisc(t("MakePayment-errorSubTitle1"));
        setUserValErrorModalState(true);
      }
    } else {
      setTitle(t("MakePayment-errorTitle2"));
      setDisc(t("MakePayment-errorSubTitle2"));
      setUserValErrorModalState(true);
    }
  }
  const makeAPayment = (data) => {
    alert("Submitting data -- " + JSON.stringify(data));
  };

  useBreadcrumb([
    {
      name: t("UnauthMakeAPayment-breadcrumb-name"),
      url: "/",
    },
    { name: t("UnauthMakeAPayment-breadcrumb-make-a-payment"), url: "#" },
  ]);

  return (
    <>
      {/*
        DEVELOPMENT NOTE:

        The interacting with the input elements controls the display of the next panel
        So the input element in the components needs the aria attributes' "aria-controls" and "aria-expands"
        No sure how to get and pass the dynamic IDs assigned by the Headless UI Disclosure between the page and the components
        We might need to change the interaction to have next buttons like the registration page

      */}
      <div className="container mb-8">
        <h1>{t("MakePayment-title")}</h1>
        {config.COMPANY_NAME === "everlake" && (
          <Trans defaults={t("MakePayment-desc")} />
        )}
        <hr />
      </div>

      {toastDetail.type && (
        <div className="ui-component-spacing">
          <Toast {...toastDetail} />
        </div>
      )}

      <form
        className="container mb-8"
        onSubmit={handleSubmit(makeAPayment)}
        ref={makeAPaymentUnauthForm}
        aria-label="make a payment form"
      >
        <div className="ui-form-make-payment container">
          {/* <!-- Step 1 --> */}
          <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-6 mb-4">
                <div
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 1
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  {t("UnauthMakeAPayment-one")}
                </div>
              </div>
              <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
            </div>
            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <div className="flex justify-between items-start">
                  <h2 className="flex flex-grow font-body font-bold text-xl">
                    {t("MakePayment-stepTitle1")}
                  </h2>
                  <div className="font-semibold text-sm whitespace-nowrap">
                    {t("MakePayment-step1")}
                  </div>
                </div>
                <p>{t("MakePayment-stepSubTitle1")}</p>

                <div className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light">
                  {currentStep >= 1 && (
                    <div className="pb-2 text-sm">
                      <PaymentPolicy
                        register={register}
                        errors={errors}
                        setCurrentStep={setCurrentStep}
                        getValues={getValues}
                        setPolicy={setPolicy}
                        setError={setError}
                        clearErrors={clearErrors}
                        isNonNaturalEntity={isNonNaturalEntity}
                        setIsNonNaturalEntity={setIsNonNaturalEntity}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* <!-- Step 2 --> */}
          <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-3 mb-4">
                <div
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 2
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  {t("UnauthMakeAPayment-two")}
                </div>
              </div>
              <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
            </div>
            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <div className="flex justify-between items-start">
                  <h2 className="flex flex-grow font-body font-bold text-xl">
                    {t("MakePayment-stepTitle2")}
                  </h2>
                  <div className="font-semibold text-sm whitespace-nowrap">
                    {t("MakePayment-step2")}
                  </div>
                </div>
                <div
                  id="ID_paymentStep2Options"
                  className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                >
                  {currentStep >= 2 && (
                    <div className="pb-2 text-sm">
                      <PaymentAmount
                        register={register}
                        setCurrentStep={setCurrentStep}
                        policy={policy}
                        setValue={setValue}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* <!-- Step 3 --> */}
          <div
            id="ID_paymentStep3"
            className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16"
          >
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-3 mb-4">
                <div
                  id="ID_paymentStep3Number"
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 3
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  {t("UnauthMakeAPayment-three")}
                </div>
              </div>
              <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
            </div>

            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <div className="flex justify-between items-start">
                  <h2 className="flex flex-grow font-body font-bold text-xl">
                    {t("MakePayment-stepTitle3")}
                  </h2>
                  <div className="font-semibold text-sm whitespace-nowrap">
                    {t("MakePayment-step3")}
                  </div>
                </div>
                <div
                  id="ID_paymentStep3Options"
                  className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                >
                  {currentStep >= 3 && (
                    <div className="pb-2 text-sm">
                      <PaymentMethod
                        register={register}
                        setValue={setValue}
                        setCurrentStep={setCurrentStep}
                        policy={policy}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* <!-- Step 4 --> */}
          <div className="flex flex-row md:ml-8 md:mr-0 lg:ml-20 xl:ml-28 lg:pr-16">
            <div className="w-8 md:flex md:flex-col md:justify-center md:mr-14">
              <div className="flex justify-center items-center mt-3 mb-4">
                <div
                  id="ID_paymentStep5Number"
                  className={`inline-flex justify-center items-center h-8 w-8 text-white ${
                    currentStep >= 4
                      ? "bg-ui-blue-dark"
                      : "bg-ui-gray-medium-dark"
                  } rounded-full overflow-hidden`}
                >
                  {t("UnauthMakeAPayment-four")}
                </div>
              </div>
              <div className="flex h-full w-px bg-transparent mx-auto overflow-hidden"></div>
            </div>

            <div className="w-full flex-grow">
              <div className="ui-card relative px-4 py-4 mb-6 lg:px-6">
                <div className="flex justify-between items-start">
                  <h2 className="flex flex-grow font-body font-bold text-xl">
                    {t("MakePayment-stepTitle4")}
                  </h2>
                  <div className="font-semibold text-sm whitespace-nowrap">
                    {t("MakePayment-step4")}
                  </div>
                </div>
                <div
                  id="ID_paymentStep5Options"
                  className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-light"
                >
                  {currentStep >= 4 && (
                    <div className="pb-2 text-sm">
                      <PaymentPayor
                        register={register}
                        validate={trigger}
                        getFieldState={getFieldState}
                        errors={errors}
                        setCurrentStep={setCurrentStep}
                        isNonNaturalEntity={isNonNaturalEntity}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="block text-right mb-8 md:ml-12 md:mr-6 lg:ml-28 lg:pr-16">
            <button
              type="button"
              className="ui-btn-secondary min-w-[140px]"
              aria-label="cancel make a payment"
              onClick={() => cancel()}
            >
              {t("MakePayment-button1")}
            </button>
            <button
              id="ID_submitPayment"
              type="button"
              className="ui-btn-primary min-w-[140px]"
              aria-label="submit payment"
              disabled={currentStep !== 5 && !isNonNaturalEntity}
              onClick={() => advanceModal()}
            >
              {t("MakePayment-button2")}
            </button>
          </div>
        </div>
      </form>

      <UnauthCustomerAuthorizePaymentModal
        modalState={authorizeModal}
        setModalState={setAuthorizeModal}
        setConfirmationModal={setConfirmationModal}
        unauthUser={true}
        submitParentData={true}
        submitFuction={handleSubmit(makeAPayment)}
        uid={uid}
        isNonNaturalEntity={isNonNaturalEntity}
      />
      <ConfirmModal
        modalState={confirmationModal}
        setModalState={setConfirmationModal}
        unauthUser={true}
      />
      <GeneralPurposeDialog
        state={userValErrorModalState}
        setState={setUserValErrorModalState}
        title={title}
        desc={disc}
      />

      <AuthorizePaymentNameValidationModal
        modalState={creditCardValidationCheckModalState}
        setModalState={setCreditCardValidationCheckModalState}
        selectedPolicy={policy}
        amount={premiumAmount}
        setToastDetail={setToastDetail}
        isNonNaturalEntity={isNonNaturalEntity}
      />
    </>
  );
}
